import { useEffect, useState } from 'react';

import { TtqibZoomableImageOverlay } from '../../features/ttqib/TtqibZoomableImageOverlay';
import { assetService } from '../../services/assetService';
import { buildAssetPreviewUrl } from '../../utils/mediaUrl';

type ProxyImageProps = {
  rawUrl?: string | null;
  ossId?: string;
  alt?: string;
  className?: string;
  imgClassName?: string;
  fallbackClassName?: string;
  fallbackText?: string;
  /** 与原始裁切一致：点击放大查看 */
  zoomable?: boolean;
  zoomTitle?: string;
};

function extractOssKeyFromUrl(rawUrl?: string | null): string {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    const key = parsed.pathname.replace(/^\/+/, '').trim();
    if (key.startsWith('assets/') || key.startsWith('ttq/')) return key;
  } catch {
    // ignore
  }
  return '';
}

function toHttps(url: string): string {
  return url.replace(/^http:\/\//i, 'https://');
}

/**
 * 私有 OSS 图：签名直链加载。
 * 禁止走 /api/media/proxy（流量禁手）。
 */
export function ProxyImage({
  rawUrl,
  ossId,
  alt = '',
  className = '',
  imgClassName = '',
  fallbackClassName = '',
  fallbackText = '暂无预览',
  zoomable = false,
  zoomTitle,
}: ProxyImageProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setRetrying(false);
    setSrc(null);

    const boot = async () => {
      const resolvedOssId = String(ossId || '').trim() || extractOssKeyFromUrl(rawUrl);

      if (resolvedOssId) {
        try {
          const signedUrl = toHttps(await assetService.getSignedOssUrl(resolvedOssId));
          if (cancelled) return;
          if (signedUrl) {
            setSrc(signedUrl);
            setFailed(false);
            return;
          }
        } catch {
          // fall through
        }
      }

      if (cancelled) return;
      // 仅直链 HTTPS；buildAssetPreviewUrl 已禁用 media proxy 包装
      const fromRaw = buildAssetPreviewUrl(rawUrl);
      setSrc(fromRaw);
      setFailed(!fromRaw);
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, [rawUrl, ossId]);

  async function handleError() {
    if (retrying) {
      setFailed(true);
      return;
    }
    const resolvedOssId = String(ossId || '').trim() || extractOssKeyFromUrl(rawUrl);
    if (!resolvedOssId) {
      setFailed(true);
      return;
    }

    setRetrying(true);
    try {
      const signedUrl = toHttps(await assetService.getSignedOssUrl(resolvedOssId));
      if (signedUrl && signedUrl !== src) {
        setSrc(signedUrl);
        setFailed(false);
        return;
      }
    } catch {
      // fall through
    }

    setFailed(true);
  }

  const image = (
    <div className={`relative overflow-hidden bg-[#EDF1F7] ${className}`}>
      {!failed && src ? (
        <img
          src={src}
          alt={alt}
          className={`h-full w-full object-contain ${imgClassName}`}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => void handleError()}
        />
      ) : (
        <div className={`flex h-full min-h-[120px] w-full items-center justify-center text-sm text-subtle ${fallbackClassName}`}>
          {failed ? fallbackText : '加载中…'}
        </div>
      )}
    </div>
  );

  if (!zoomable || !src || failed) return image;

  return (
    <>
      <button
        type="button"
        className="block w-full cursor-zoom-in text-left"
        onClick={() => setViewerOpen(true)}
        title="点击放大查看细节"
      >
        {image}
      </button>
      <TtqibZoomableImageOverlay
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title={zoomTitle || alt || '图片'}
        fullSourceUrl={src}
      />
    </>
  );
}

export function useAssetFeedColumns() {
  const [columns, setColumns] = useState(() => resolveColumnCount(window.innerWidth));

  useEffect(() => {
    function handleResize() {
      setColumns(resolveColumnCount(window.innerWidth));
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return columns;
}

function resolveColumnCount(width: number) {
  if (width >= 1280) return 4;
  if (width >= 960) return 3;
  return 2;
}
